import { createPart, type PagePart, type PartType } from '@/features/storefront/sections/parts';
import { defaultSection } from '@/features/storefront/sections/pages';
import { createSection, type Section } from '@/features/storefront/sections/schema';

/**
 * Pawly's product page, written down.
 *
 * A pet-shop product page: the pack photographed large on an oat tile on the
 * left, and on the right the brand, the name in tall capitals, the price with
 * what it was, pack size, stock, how many, add to cart and buy it now, then the
 * folded panels - description, specifications, shipping and returns. Under
 * both, what else the pet might like, then the reviews on an oat band.
 *
 * The reference design also pins a small add-to-cart bar to the bottom of the
 * window once the buy column scrolls away, and runs a sale countdown above the
 * pack sizes. Neither is a part the storefront has, so neither is here; the
 * buy column sticks beside the gallery instead (see `Product.tsx`), which
 * keeps the button in reach on a wide screen.
 *
 * As with the other themes' product pages, these are *defaults*: editable,
 * movable and removable in the customiser, carrying nothing that belongs to
 * the sample shop. The shipping panel draws nothing until the merchant writes
 * it.
 */

/**
 * One component of the page, with an id that does not change between renders
 * - see Hyper's `productPage.ts` for why a default must not mint a fresh id.
 */
function part(
  id: string,
  type: PartType,
  region: string,
  over: Partial<Omit<PagePart, 'id' | 'type'>> = {},
): PagePart {
  const base = createPart(type, region);
  return {
    ...base,
    ...over,
    id: `pawly-${id}`,
    region,
    options: { ...base.options, ...(over.options ?? {}) },
  };
}

/** The components of the product, in the regions `Product.tsx` lays out. */
export function pawlyProductParts(): PagePart[] {
  return [
    part('breadcrumbs', 'product_breadcrumbs', 'top'),

    part('gallery', 'product_gallery', 'gallery'),

    part('brand', 'product_brand', 'main'),
    part('title', 'product_title', 'main'),
    part('price', 'product_price', 'main', {
      options: { show_compare_at: true, show_saving: true },
    }),
    part('summary', 'product_short_description', 'main'),
    part('variants', 'product_variants', 'main'),
    part('availability', 'product_availability', 'main'),
    part('quantity', 'product_quantity', 'main'),
    part('add', 'product_add_to_cart', 'main', { width: 'full' }),
    part('buy', 'product_buy_now', 'main', { width: 'full', options: { style: 'solid' } }),

    part('description', 'product_description', 'main', {
      label: 'Description',
      space_above: 'lg',
      options: { show_heading: true, collapsible: true },
    }),
    part('specs', 'product_specs', 'main', {
      label: 'Specifications',
      options: { collapsible: true },
    }),
    part('delivery', 'product_shipping_info', 'main', {
      label: 'Shipping and returns',
      body: '',
      options: { collapsible: true, icon: 'truck' },
    }),
    part('share', 'product_share', 'main'),

    part('related', 'product_related', 'below'),
  ];
}

/** A section with a stable id, for the same reason {@link part} fixes one. */
function fixed(id: string, section: Section): Section {
  return { ...section, id: `pawly-${id}` };
}

/** The product page as a merchant on this theme first finds it. */
export function pawlyProductSections(): Section[] {
  return [
    defaultSection('product_details'),
    fixed('reviews', {
      ...createSection('product_reviews'),
      title: 'Customer reviews',
      background: 'surface',
      padding: 'lg',
    }),
  ];
}
